import { useCallback, useEffect, useState } from 'react';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { Modal, FormField } from '../components/modal';
import { ConfirmDialog } from '../components/confirm-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Link } from '../lib/routing';
import { cn } from '@/lib/utils';
import { FolderSimple, Plus, PencilSimple, WarningCircle, ArrowClockwise } from '@phosphor-icons/react';
import {
  getMySchoolId,
  listClassrooms,
  listClassGroups,
  createClassGroup,
  renameClassGroup,
  setClassGroupClassrooms,
  deleteClassGroup,
  type Classroom,
  type ClassGroup,
} from '../lib/direction';

const listShellClass = "bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] overflow-hidden divide-y divide-[var(--naia-line)]";
const pickBase = "inline-flex items-center gap-[7px] min-h-[38px] rounded-full border-[1.5px] border-[var(--naia-line)] bg-transparent text-[var(--naia-text-secondary)] font-bold text-[13px] px-[14px] py-[6px] cursor-pointer font-[inherit] hover:border-primary transition-colors";

type Editing = ClassGroup | 'new' | null;

export default function DirectionGroups() {
  const [schoolId, setSchoolId] = useState<string | null>(null);
  const [groups, setGroups] = useState<ClassGroup[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<Editing>(null);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<ClassGroup | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const sid = await getMySchoolId();
      setSchoolId(sid);
      if (sid) {
        const [gs, cs] = await Promise.all([listClassGroups(), listClassrooms(sid)]);
        setGroups(gs);
        setClassrooms(cs.filter((c) => !c.archived));
      }
    } catch {
      setGroups([]);
      setClassrooms([]);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const reload = load;

  const openNew = () => { setEditing('new'); setName(''); setSelected(new Set()); };
  const openEdit = (grp: ClassGroup) => {
    setEditing(grp);
    setName(grp.name);
    setSelected(new Set(grp.classrooms.map((c) => c.id)));
  };
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    setNotice(null);
    try {
      const ids = [...selected];
      if (editing === 'new') {
        const created = await createClassGroup(trimmed);
        if (ids.length) await setClassGroupClassrooms(created.id, ids);
        setNotice(`Groupe « ${trimmed} » créé.`);
      } else if (editing) {
        if (editing.name !== trimmed) await renameClassGroup(editing.id, trimmed);
        await setClassGroupClassrooms(editing.id, ids);
        setNotice(`Groupe « ${trimmed} » mis à jour.`);
      }
      await reload();
      setEditing(null);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setNotice(null);
    try {
      await deleteClassGroup(deleting.id);
      setGroups((p) => p.filter((x) => x.id !== deleting.id));
      setDeleting(null);
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Échec de la suppression.');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <>
      <HeaderContent
        width="md"
        showBurger
        eyebrow="Mode direction"
        eyebrowIcon={<FolderSimple size={11} weight="bold" />}
        actions={!failed && schoolId && <Button onClick={openNew}><Plus size={16} /> Nouveau groupe</Button>}
      >
        <div className="min-w-0">
          <h1 className={HEADER_TITLE_CLASS}>Groupes de classes</h1>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Regroupez des classes pour déléguer leur supervision à un membre de l'équipe de direction.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-5 pb-16 pt-4">

        {notice && (
          <p className="mb-4 px-[14px] py-[10px] rounded-[10px] bg-primary/10 text-primary text-sm font-semibold">
            {notice}
          </p>
        )}

        {loading ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Chargement…</p>
        ) : failed ? (
          <div className="flex flex-col items-start gap-3 py-4">
            <p className="flex items-start gap-2 m-0 text-[13.5px] text-[var(--naia-text-primary)]">
              <WarningCircle size={18} weight="fill" className="text-destructive shrink-0 mt-px" />
              <span>Vos groupes de classes n'ont pas pu être chargés. Ils sont intacts.</span>
            </p>
            <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => void load()}>
              <ArrowClockwise size={15} weight="bold" />
              Réessayer
            </Button>
          </div>
        ) : !schoolId ? (
          <p className="text-[var(--naia-text-secondary)] py-4">Aucun établissement rattaché.</p>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-[10px] text-center px-6 py-10 bg-[var(--naia-card)] border border-dashed border-[var(--naia-line)] rounded-[var(--naia-r-card)]">
            <div className="grid place-items-center w-[52px] h-[52px] rounded-full bg-primary/10 text-primary"><FolderSimple size={26} /></div>
            <div className="font-extrabold text-[16px]">Aucun groupe pour l'instant</div>
            <p className="text-[13.5px] text-[var(--naia-text-secondary)] max-w-[360px] m-0 mb-1 leading-relaxed">
              Créez un groupe (ex. « 6ᵉ », « Cycle 4 », « Classes de M. Durand ») et ajoutez-y des classes.
            </p>
            <Button onClick={openNew}><Plus size={16} /> Nouveau groupe</Button>
          </div>
        ) : (
          <div className={listShellClass}>
            {groups.map((grp) => (
              <div key={grp.id} className="flex flex-col gap-[10px] px-4 py-[15px]">
                <div className="flex items-center gap-2">
                  <span className="font-extrabold text-[16px] flex-1 min-w-0 break-words text-[var(--naia-text-primary)]">{grp.name}</span>
                  <span className="text-[11px] font-extrabold tracking-[0.3px] whitespace-nowrap text-primary bg-primary/10 rounded-full px-[10px] py-[3px]">
                    {grp.classrooms.length} classe{grp.classrooms.length > 1 ? 's' : ''}
                  </span>
                  <Button variant="ghost" size="icon" className="w-7 h-7 min-h-0 shrink-0" onClick={() => openEdit(grp)} title="Modifier le groupe" aria-label="Modifier le groupe"><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="icon" className="w-7 h-7 min-h-0 shrink-0" onClick={() => setDeleting(grp)} title="Supprimer le groupe" aria-label="Supprimer le groupe">×</Button>
                </div>
                {grp.classrooms.length === 0 ? (
                  <span className="text-[13px] text-[var(--naia-text-secondary)]">Aucune classe — cliquez sur <PencilSimple size={12} className="inline" /> pour en ajouter.</span>
                ) : (
                  <div className="flex flex-wrap gap-[6px]">
                    {grp.classrooms.map((c) => (
                      <span key={c.id} className="inline-flex items-center gap-[7px] text-[12.5px] font-bold text-[var(--naia-text-primary)] bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-full px-[11px] py-1">
                        {c.name}
                        {c.level && <span className="text-[10px] font-extrabold text-[var(--naia-text-secondary)] uppercase tracking-[0.5px]">{c.level}</span>}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <Modal
        open={editing !== null}
        title={editing === 'new' ? 'Nouveau groupe de classes' : 'Modifier le groupe'}
        onClose={() => setEditing(null)}
        footer={<>
          <Button variant="secondary" onClick={() => setEditing(null)}>Annuler</Button>
          <Button onClick={submit} disabled={saving || !name.trim()}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>}
      >
        <FormField label="Nom du groupe" value={name} onChange={setName} placeholder="ex. Cycle 4" autoFocus />
        <div className="text-[11px] font-black tracking-[1px] text-[var(--naia-text-secondary)] uppercase mt-[14px] mb-2">Classes du groupe</div>
        {failed ? (
          <p className="flex items-start gap-2 py-2 m-0 text-[13px] text-[var(--naia-text-primary)]">
            <WarningCircle size={16} weight="fill" className="text-destructive shrink-0 mt-px" />
            <span>Les classes de l'établissement n'ont pas pu être chargées. Fermez et réessayez.</span>
          </p>
        ) : classrooms.length === 0 ? (
          <div className="flex flex-col items-start gap-2 py-2">
            <p className="text-[13px] text-[var(--naia-text-secondary)] m-0">Un groupe rassemble des classes, et l'établissement n'en a aucune.</p>
            <Link to="/direction" className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), 'no-underline')}>
              <Plus size={14} /> Créer une classe
            </Link>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2 max-h-[46vh] overflow-y-auto p-0.5">
            {classrooms.map((c) => {
              const on = selected.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggle(c.id)}
                  aria-pressed={on}
                  className={cn(pickBase, on && "border-primary bg-primary text-primary-foreground hover:border-primary")}
                >
                  {on ? '✓ ' : ''}{c.name}
                  {c.level && <span className="text-[10px] font-extrabold opacity-75 uppercase tracking-[0.5px]">{c.level}</span>}
                </button>
              );
            })}
          </div>
        )}
        <p className="text-[12px] text-[var(--naia-text-secondary)] mt-[10px]">{selected.size} classe{selected.size > 1 ? 's' : ''} sélectionnée{selected.size > 1 ? 's' : ''}.</p>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Supprimer ce groupe ?"
        description={deleting && (
          <>« {deleting.name} » sera supprimé. Les classes qu'il contient ne sont pas supprimées.</>
        )}
        confirmLabel={deleteBusy ? 'Suppression…' : 'Supprimer'}
        busy={deleteBusy}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
